import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// Helper function to make authenticated API calls
async function authenticatedApiCall(url: string, options: RequestInit = {}) {
  const { authenticatedFetch } = await import("../auth/apiInterceptor");
  return authenticatedFetch(url, options);
}

export interface Department {
  id: number;
  name: string;
}

export interface HiringManager {
  id: number;
  email: string;
  first_name: string;
  middle_name: string;
  last_name: string;
  designation: string;
  gender: string;
  company: {
    id: number;
    name: string;
    profile_picture: string | null;
    website: string | null;
    email: string | null;
    contact_number: string | null;
    industry: string | null;
    country: string | null;
    city: string | null;
    address_line_1: string | null;
    address_line_2: string | null;
    about: string | null;
  };
  department: {
    id: number;
    name: string;
  };
  profile_picture: string | null;
}

export interface JobTitle {
  id: number;
  name: string;
}

export interface JobLevel {
  id: number;
  name: string;
}

export interface Region {
  id: number;
  name: string;
}

export interface Country {
  id: number;
  name: string;
}

export interface City {
  id: number;
  name: string;
}

export interface EmploymentType {
  id: string;
  name: string;
}

export interface WorkplaceType {
  id: string;
  name: string;
}

export interface Industry {
  id: number;
  name: string;
}

export interface FieldOfExpertise {
  id: number;
  name: string;
}

export interface KeySkill {
  id: number;
  name: string;
}

export interface Language {
  id: number;
  name: string;
}

export interface SpeakingLevel {
  value: string;
  label: string;
}

export interface ReadingWritingLevel {
  value: string;
  label: string;
}

export interface Qualification {
  id: number;
  name: string;
}

export interface FieldOfStudy {
  id: number;
  name: string;
}

export interface Certificate {
  id: number;
  name: string;
}

export interface GenderOption {
  value: string;
  label: string;
}

export interface VisaStatusOption {
  value: string;
  label: string;
}

export interface DrivingLicenseOption {
  value: string;
  label: string;
}

export interface ManagementSkill {
  id: number;
  name: string;
}

interface FiltersState {
  departments: Department[];
  departmentsStatus: "idle" | "loading" | "succeeded" | "failed";
  departmentsError: string | null;
  hiringManagers: HiringManager[];
  hiringManagersStatus: "idle" | "loading" | "succeeded" | "failed";
  hiringManagersError: string | null;
  jobTitles: JobTitle[];
  jobTitlesStatus: "idle" | "loading" | "succeeded" | "failed";
  jobTitlesError: string | null;
  jobLevels: JobLevel[];
  jobLevelsStatus: "idle" | "loading" | "succeeded" | "failed";
  jobLevelsError: string | null;
  regions: Region[];
  regionsStatus: "idle" | "loading" | "succeeded" | "failed";
  regionsError: string | null;
  countries: Country[];
  countriesStatus: "idle" | "loading" | "succeeded" | "failed";
  countriesError: string | null;
  nationalityCountries: Country[];
  nationalityCountriesStatus: "idle" | "loading" | "succeeded" | "failed";
  nationalityCountriesError: string | null;
  cities: City[];
  citiesStatus: "idle" | "loading" | "succeeded" | "failed";
  citiesError: string | null;
  employmentTypes: EmploymentType[];
  employmentTypesStatus: "idle" | "loading" | "succeeded" | "failed";
  employmentTypesError: string | null;
  workplaceTypes: WorkplaceType[];
  workplaceTypesStatus: "idle" | "loading" | "succeeded" | "failed";
  workplaceTypesError: string | null;
  industries: Industry[];
  industriesStatus: "idle" | "loading" | "succeeded" | "failed";
  industriesError: string | null;
  fieldsOfExpertise: FieldOfExpertise[];
  fieldsOfExpertiseStatus: "idle" | "loading" | "succeeded" | "failed";
  fieldsOfExpertiseError: string | null;
  keySkills: KeySkill[];
  keySkillsStatus: "idle" | "loading" | "succeeded" | "failed";
  keySkillsError: string | null;
  languages: Language[];
  languagesStatus: "idle" | "loading" | "succeeded" | "failed";
  languagesError: string | null;
  speakingLevels: SpeakingLevel[];
  speakingLevelsStatus: "idle" | "loading" | "succeeded" | "failed";
  speakingLevelsError: string | null;
  readingWritingLevels: ReadingWritingLevel[];
  readingWritingLevelsStatus: "idle" | "loading" | "succeeded" | "failed";
  readingWritingLevelsError: string | null;
  qualifications: Qualification[];
  qualificationsStatus: "idle" | "loading" | "succeeded" | "failed";
  qualificationsError: string | null;
  fieldsOfStudy: FieldOfStudy[];
  fieldsOfStudyStatus: "idle" | "loading" | "succeeded" | "failed";
  fieldsOfStudyError: string | null;
  certificates: Certificate[];
  certificatesStatus: "idle" | "loading" | "succeeded" | "failed";
  certificatesError: string | null;
  genders: GenderOption[];
  gendersStatus: "idle" | "loading" | "succeeded" | "failed";
  gendersError: string | null;
  visaStatuses: VisaStatusOption[];
  visaStatusesStatus: "idle" | "loading" | "succeeded" | "failed";
  visaStatusesError: string | null;
  drivingLicenses: DrivingLicenseOption[];
  drivingLicensesStatus: "idle" | "loading" | "succeeded" | "failed";
  drivingLicensesError: string | null;
  managementSkills: ManagementSkill[];
  managementSkillsStatus: "idle" | "loading" | "succeeded" | "failed";
  managementSkillsError: string | null;
}

const initialState: FiltersState = {
  departments: [],
  departmentsStatus: "idle",
  departmentsError: null,
  hiringManagers: [],
  hiringManagersStatus: "idle",
  hiringManagersError: null,
  jobTitles: [],
  jobTitlesStatus: "idle",
  jobTitlesError: null,
  jobLevels: [],
  jobLevelsStatus: "idle",
  jobLevelsError: null,
  regions: [],
  regionsStatus: "idle",
  regionsError: null,
  countries: [],
  countriesStatus: "idle",
  countriesError: null,
  nationalityCountries: [],
  nationalityCountriesStatus: "idle",
  nationalityCountriesError: null,
  cities: [],
  citiesStatus: "idle",
  citiesError: null,
  employmentTypes: [],
  employmentTypesStatus: "idle",
  employmentTypesError: null,
  workplaceTypes: [],
  workplaceTypesStatus: "idle",
  workplaceTypesError: null,
  industries: [],
  industriesStatus: "idle",
  industriesError: null,
  fieldsOfExpertise: [],
  fieldsOfExpertiseStatus: "idle",
  fieldsOfExpertiseError: null,
  keySkills: [],
  keySkillsStatus: "idle",
  keySkillsError: null,
  languages: [],
  languagesStatus: "idle",
  languagesError: null,
  speakingLevels: [],
  speakingLevelsStatus: "idle",
  speakingLevelsError: null,
  readingWritingLevels: [],
  readingWritingLevelsStatus: "idle",
  readingWritingLevelsError: null,
  qualifications: [],
  qualificationsStatus: "idle",
  qualificationsError: null,
  fieldsOfStudy: [],
  fieldsOfStudyStatus: "idle",
  fieldsOfStudyError: null,
  certificates: [],
  certificatesStatus: "idle",
  certificatesError: null,
  genders: [],
  gendersStatus: "idle",
  gendersError: null,
  visaStatuses: [],
  visaStatusesStatus: "idle",
  visaStatusesError: null,
  drivingLicenses: [],
  drivingLicensesStatus: "idle",
  drivingLicensesError: null,
  managementSkills: [],
  managementSkillsStatus: "idle",
  managementSkillsError: null,
};

export const fetchDepartments = createAsyncThunk<Department[], void>(
  "filters/fetchDepartments",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/department/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching departments");
      }
      return (data.data as Department[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching departments");
    }
  }
);

export const fetchHiringManagers = createAsyncThunk<
  HiringManager[],
  { departmentId: number; companyId: number; accessToken: string },
  { rejectValue: { message: string } }
>(
  "filters/fetchHiringManagers",
  async ({ departmentId, companyId, accessToken }, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(
        `${baseUrl}/api/company/member/hiring-managers/department/${departmentId}/${companyId}/`,
        {
          method: "GET",
        }
      );
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching hiring managers");
      }
      return (data.data as HiringManager[]);
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "Error fetching hiring managers",
      });
    }
  }
);

export const fetchJobTitles = createAsyncThunk<JobTitle[], void>(
  "filters/fetchJobTitles",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/job-title/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching job titles");
      }
      return (data.data as JobTitle[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching job titles");
    }
  }
);

export const fetchJobLevels = createAsyncThunk<JobLevel[], void>(
  "filters/fetchJobLevels",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/years-of-professional-experience/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching job levels");
      }
      return (data.data as JobLevel[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching job levels");
    }
  }
);

export const fetchRegions = createAsyncThunk<Region[], void>(
  "filters/fetchRegions",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/region/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching regions");
      }
      return (data.data as Region[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching regions");
    }
  }
);

export const fetchCountries = createAsyncThunk<
  Country[],
  { regionId: number },
  { rejectValue: { message: string } }
>(
  "filters/fetchCountries",
  async ({ regionId }, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(
        `${baseUrl}/api/filter/country/?region_ids=${regionId}`
      );
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching countries");
      }
      return (data.data as Country[]);
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "Error fetching countries",
      });
    }
  }
);

/** All countries for nationality (no region filter). */
export const fetchNationalityCountries = createAsyncThunk<Country[], void>(
  "filters/fetchNationalityCountries",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/nationality/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching countries");
      }
      return data.data as Country[];
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching countries");
    }
  }
);

export const fetchCities = createAsyncThunk<
  City[],
  { countryId: number },
  { rejectValue: { message: string } }
>(
  "filters/fetchCities",
  async ({ countryId }, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(
        `${baseUrl}/api/filter/city/?country_ids=${countryId}`
      );
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching cities");
      }
      return (data.data as City[]);
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "Error fetching cities",
      });
    }
  }
);

export const fetchEmploymentTypes = createAsyncThunk<EmploymentType[], void>(
  "filters/fetchEmploymentTypes",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/employment-type/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching employment types");
      }
      return (data.data as EmploymentType[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching employment types");
    }
  }
);

export const fetchWorkplaceTypes = createAsyncThunk<WorkplaceType[], void>(
  "filters/fetchWorkplaceTypes",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/workplace-type/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching workplace types");
      }
      return (data.data as WorkplaceType[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching workplace types");
    }
  }
);

export const fetchIndustries = createAsyncThunk<Industry[], void>(
  "filters/fetchIndustries",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/industry/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching industries");
      }
      return (data.data as Industry[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching industries");
    }
  }
);

export const fetchFieldsOfExpertise = createAsyncThunk<FieldOfExpertise[], void>(
  "filters/fetchFieldsOfExpertise",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/field-of-expertise/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching fields of expertise");
      }
      return (data.data as FieldOfExpertise[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching fields of expertise");
    }
  }
);

export const fetchKeySkills = createAsyncThunk<
  KeySkill[],
  { fieldOfExpertiseId: number },
  { rejectValue: { message: string } }
>(
  "filters/fetchKeySkills",
  async ({ fieldOfExpertiseId }, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(
        `${baseUrl}/api/filter/key-skill/?field_of_expertise_ids=${fieldOfExpertiseId}`
      );
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching key skills");
      }
      return (data.data as KeySkill[]);
    } catch (error: any) {
      return rejectWithValue({
        message: error.message || "Error fetching key skills",
      });
    }
  }
);

export const fetchLanguages = createAsyncThunk<Language[], void>(
  "filters/fetchLanguages",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/language/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching languages");
      }
      return (data.data as Language[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching languages");
    }
  }
);

export const fetchSpeakingLevels = createAsyncThunk<SpeakingLevel[], void>(
  "filters/fetchSpeakingLevels",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/language-speaking-level/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching speaking levels");
      }
      return (data.data as SpeakingLevel[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching speaking levels");
    }
  }
);

export const fetchReadingWritingLevels = createAsyncThunk<ReadingWritingLevel[], void>(
  "filters/fetchReadingWritingLevels",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/language-reading-writing-level/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching reading/writing levels");
      }
      return (data.data as ReadingWritingLevel[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching reading/writing levels");
    }
  }
);

export const fetchQualifications = createAsyncThunk<Qualification[], void>(
  "filters/fetchQualifications",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/qualification/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching qualifications");
      }
      return (data.data as Qualification[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching qualifications");
    }
  }
);

export const fetchFieldsOfStudy = createAsyncThunk<FieldOfStudy[], void>(
  "filters/fetchFieldsOfStudy",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/field-of-study/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching fields of study");
      }
      return (data.data as FieldOfStudy[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching fields of study");
    }
  }
);

export const fetchCertificates = createAsyncThunk<Certificate[], void>(
  "filters/fetchCertificates",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/certificate/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching certificates");
      }
      return (data.data as Certificate[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching certificates");
    }
  }
);

export const fetchGenders = createAsyncThunk<GenderOption[], void>(
  "filters/fetchGenders",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/gender/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching genders");
      }
      return (data.data as GenderOption[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching genders");
    }
  }
);

export const fetchVisaStatuses = createAsyncThunk<VisaStatusOption[], void>(
  "filters/fetchVisaStatuses",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/job/job-visa-requirement/list/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching visa statuses");
      }
      return (data.data as VisaStatusOption[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching visa statuses");
    }
  }
);

export const fetchDrivingLicenses = createAsyncThunk<DrivingLicenseOption[], void>(
  "filters/fetchDrivingLicenses",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/job/job-driving-license-requirement/list/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching driving licenses");
      }
      return (data.data as DrivingLicenseOption[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching driving licenses");
    }
  }
);

export const fetchManagementSkills = createAsyncThunk<ManagementSkill[], void>(
  "filters/fetchManagementSkills",
  async (_, { rejectWithValue }) => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authenticatedApiCall(`${baseUrl}/api/filter/management-skill/`);
      const data = await res.json();
      if (!res.ok || data.code !== 0) {
        throw new Error(data.message || "Error fetching management skills");
      }
      return (data.data as ManagementSkill[]);
    } catch (error: any) {
      return rejectWithValue(error.message || "Error fetching management skills");
    }
  }
);

const filterSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    clearHiringManagers: (state) => {
      state.hiringManagers = [];
      state.hiringManagersStatus = "idle";
      state.hiringManagersError = null;
    },
    clearCountries: (state) => {
      state.countries = [];
      state.countriesStatus = "idle";
      state.countriesError = null;
    },
    clearCities: (state) => {
      state.cities = [];
      state.citiesStatus = "idle";
      state.citiesError = null;
    },
    clearKeySkills: (state) => {
      state.keySkills = [];
      state.keySkillsStatus = "idle";
      state.keySkillsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDepartments.pending, (state) => {
        state.departmentsStatus = "loading";
        state.departmentsError = null;
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.departments = action.payload;
        state.departmentsStatus = "succeeded";
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.departmentsStatus = "failed";
        state.departmentsError = action.error.message || "Failed to fetch departments.";
      })
      .addCase(fetchHiringManagers.pending, (state) => {
        state.hiringManagersStatus = "loading";
        state.hiringManagersError = null;
      })
      .addCase(fetchHiringManagers.fulfilled, (state, action) => {
        state.hiringManagers = action.payload;
        state.hiringManagersStatus = "succeeded";
      })
      .addCase(fetchHiringManagers.rejected, (state, action) => {
        state.hiringManagersStatus = "failed";
        state.hiringManagersError =
          action.payload?.message || "Failed to fetch hiring managers.";
      })
      .addCase(fetchJobTitles.pending, (state) => {
        state.jobTitlesStatus = "loading";
        state.jobTitlesError = null;
      })
      .addCase(fetchJobTitles.fulfilled, (state, action) => {
        state.jobTitles = action.payload;
        state.jobTitlesStatus = "succeeded";
      })
      .addCase(fetchJobTitles.rejected, (state, action) => {
        state.jobTitlesStatus = "failed";
        state.jobTitlesError = action.error.message || "Failed to fetch job titles.";
      })
      .addCase(fetchJobLevels.pending, (state) => {
        state.jobLevelsStatus = "loading";
        state.jobLevelsError = null;
      })
      .addCase(fetchJobLevels.fulfilled, (state, action) => {
        state.jobLevels = action.payload;
        state.jobLevelsStatus = "succeeded";
      })
      .addCase(fetchJobLevels.rejected, (state, action) => {
        state.jobLevelsStatus = "failed";
        state.jobLevelsError = action.error.message || "Failed to fetch job levels.";
      })
      .addCase(fetchRegions.pending, (state) => {
        state.regionsStatus = "loading";
        state.regionsError = null;
      })
      .addCase(fetchRegions.fulfilled, (state, action) => {
        state.regions = action.payload;
        state.regionsStatus = "succeeded";
      })
      .addCase(fetchRegions.rejected, (state, action) => {
        state.regionsStatus = "failed";
        state.regionsError = action.error.message || "Failed to fetch regions.";
      })
      .addCase(fetchCountries.pending, (state) => {
        state.countriesStatus = "loading";
        state.countriesError = null;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.countries = action.payload;
        state.countriesStatus = "succeeded";
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        state.countriesStatus = "failed";
        state.countriesError =
          action.payload?.message || "Failed to fetch countries.";
      })
      .addCase(fetchNationalityCountries.pending, (state) => {
        state.nationalityCountriesStatus = "loading";
        state.nationalityCountriesError = null;
      })
      .addCase(fetchNationalityCountries.fulfilled, (state, action) => {
        state.nationalityCountries = action.payload;
        state.nationalityCountriesStatus = "succeeded";
      })
      .addCase(fetchNationalityCountries.rejected, (state, action) => {
        state.nationalityCountriesStatus = "failed";
        state.nationalityCountriesError =
          action.error.message || "Failed to fetch nationality countries.";
      })
      .addCase(fetchCities.pending, (state) => {
        state.citiesStatus = "loading";
        state.citiesError = null;
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.cities = action.payload;
        state.citiesStatus = "succeeded";
      })
      .addCase(fetchCities.rejected, (state, action) => {
        state.citiesStatus = "failed";
        state.citiesError = action.payload?.message || "Failed to fetch cities.";
      })
      .addCase(fetchEmploymentTypes.pending, (state) => {
        state.employmentTypesStatus = "loading";
        state.employmentTypesError = null;
      })
      .addCase(fetchEmploymentTypes.fulfilled, (state, action) => {
        state.employmentTypes = action.payload;
        state.employmentTypesStatus = "succeeded";
      })
      .addCase(fetchEmploymentTypes.rejected, (state, action) => {
        state.employmentTypesStatus = "failed";
        state.employmentTypesError = action.error.message || "Failed to fetch employment types.";
      })
      .addCase(fetchWorkplaceTypes.pending, (state) => {
        state.workplaceTypesStatus = "loading";
        state.workplaceTypesError = null;
      })
      .addCase(fetchWorkplaceTypes.fulfilled, (state, action) => {
        state.workplaceTypes = action.payload;
        state.workplaceTypesStatus = "succeeded";
      })
      .addCase(fetchWorkplaceTypes.rejected, (state, action) => {
        state.workplaceTypesStatus = "failed";
        state.workplaceTypesError = action.error.message || "Failed to fetch workplace types.";
      })
      .addCase(fetchIndustries.pending, (state) => {
        state.industriesStatus = "loading";
        state.industriesError = null;
      })
      .addCase(fetchIndustries.fulfilled, (state, action) => {
        state.industries = action.payload;
        state.industriesStatus = "succeeded";
      })
      .addCase(fetchIndustries.rejected, (state, action) => {
        state.industriesStatus = "failed";
        state.industriesError = action.error.message || "Failed to fetch industries.";
      })
      .addCase(fetchFieldsOfExpertise.pending, (state) => {
        state.fieldsOfExpertiseStatus = "loading";
        state.fieldsOfExpertiseError = null;
      })
      .addCase(fetchFieldsOfExpertise.fulfilled, (state, action) => {
        state.fieldsOfExpertise = action.payload;
        state.fieldsOfExpertiseStatus = "succeeded";
      })
      .addCase(fetchFieldsOfExpertise.rejected, (state, action) => {
        state.fieldsOfExpertiseStatus = "failed";
        state.fieldsOfExpertiseError = action.error.message || "Failed to fetch fields of expertise.";
      })
      .addCase(fetchKeySkills.pending, (state) => {
        state.keySkillsStatus = "loading";
        state.keySkillsError = null;
      })
      .addCase(fetchKeySkills.fulfilled, (state, action) => {
        state.keySkills = action.payload;
        state.keySkillsStatus = "succeeded";
      })
      .addCase(fetchKeySkills.rejected, (state, action) => {
        state.keySkillsStatus = "failed";
        state.keySkillsError = action.payload?.message || "Failed to fetch key skills.";
      })
      .addCase(fetchLanguages.pending, (state) => {
        state.languagesStatus = "loading";
        state.languagesError = null;
      })
      .addCase(fetchLanguages.fulfilled, (state, action) => {
        state.languages = action.payload;
        state.languagesStatus = "succeeded";
      })
      .addCase(fetchLanguages.rejected, (state, action) => {
        state.languagesStatus = "failed";
        state.languagesError = action.error.message || "Failed to fetch languages.";
      })
      .addCase(fetchSpeakingLevels.pending, (state) => {
        state.speakingLevelsStatus = "loading";
        state.speakingLevelsError = null;
      })
      .addCase(fetchSpeakingLevels.fulfilled, (state, action) => {
        state.speakingLevels = action.payload;
        state.speakingLevelsStatus = "succeeded";
      })
      .addCase(fetchSpeakingLevels.rejected, (state, action) => {
        state.speakingLevelsStatus = "failed";
        state.speakingLevelsError = action.error.message || "Failed to fetch speaking levels.";
      })
      .addCase(fetchReadingWritingLevels.pending, (state) => {
        state.readingWritingLevelsStatus = "loading";
        state.readingWritingLevelsError = null;
      })
      .addCase(fetchReadingWritingLevels.fulfilled, (state, action) => {
        state.readingWritingLevels = action.payload;
        state.readingWritingLevelsStatus = "succeeded";
      })
      .addCase(fetchReadingWritingLevels.rejected, (state, action) => {
        state.readingWritingLevelsStatus = "failed";
        state.readingWritingLevelsError = action.error.message || "Failed to fetch reading/writing levels.";
      })
      .addCase(fetchQualifications.pending, (state) => {
        state.qualificationsStatus = "loading";
        state.qualificationsError = null;
      })
      .addCase(fetchQualifications.fulfilled, (state, action) => {
        state.qualifications = action.payload;
        state.qualificationsStatus = "succeeded";
      })
      .addCase(fetchQualifications.rejected, (state, action) => {
        state.qualificationsStatus = "failed";
        state.qualificationsError = action.error.message || "Failed to fetch qualifications.";
      })
      .addCase(fetchFieldsOfStudy.pending, (state) => {
        state.fieldsOfStudyStatus = "loading";
        state.fieldsOfStudyError = null;
      })
      .addCase(fetchFieldsOfStudy.fulfilled, (state, action) => {
        state.fieldsOfStudy = action.payload;
        state.fieldsOfStudyStatus = "succeeded";
      })
      .addCase(fetchFieldsOfStudy.rejected, (state, action) => {
        state.fieldsOfStudyStatus = "failed";
        state.fieldsOfStudyError = action.error.message || "Failed to fetch fields of study.";
      })
      .addCase(fetchCertificates.pending, (state) => {
        state.certificatesStatus = "loading";
        state.certificatesError = null;
      })
      .addCase(fetchCertificates.fulfilled, (state, action) => {
        state.certificates = action.payload;
        state.certificatesStatus = "succeeded";
      })
      .addCase(fetchCertificates.rejected, (state, action) => {
        state.certificatesStatus = "failed";
        state.certificatesError = action.error.message || "Failed to fetch certificates.";
      })
      .addCase(fetchGenders.pending, (state) => {
        state.gendersStatus = "loading";
        state.gendersError = null;
      })
      .addCase(fetchGenders.fulfilled, (state, action) => {
        state.genders = action.payload;
        state.gendersStatus = "succeeded";
      })
      .addCase(fetchGenders.rejected, (state, action) => {
        state.gendersStatus = "failed";
        state.gendersError = action.error.message || "Failed to fetch genders.";
      })
      .addCase(fetchVisaStatuses.pending, (state) => {
        state.visaStatusesStatus = "loading";
        state.visaStatusesError = null;
      })
      .addCase(fetchVisaStatuses.fulfilled, (state, action) => {
        state.visaStatuses = action.payload;
        state.visaStatusesStatus = "succeeded";
      })
      .addCase(fetchVisaStatuses.rejected, (state, action) => {
        state.visaStatusesStatus = "failed";
        state.visaStatusesError = action.error.message || "Failed to fetch visa statuses.";
      })
      .addCase(fetchDrivingLicenses.pending, (state) => {
        state.drivingLicensesStatus = "loading";
        state.drivingLicensesError = null;
      })
      .addCase(fetchDrivingLicenses.fulfilled, (state, action) => {
        state.drivingLicenses = action.payload;
        state.drivingLicensesStatus = "succeeded";
      })
      .addCase(fetchDrivingLicenses.rejected, (state, action) => {
        state.drivingLicensesStatus = "failed";
        state.drivingLicensesError = action.error.message || "Failed to fetch driving licenses.";
      })
      .addCase(fetchManagementSkills.pending, (state) => {
        state.managementSkillsStatus = "loading";
        state.managementSkillsError = null;
      })
      .addCase(fetchManagementSkills.fulfilled, (state, action) => {
        state.managementSkills = action.payload;
        state.managementSkillsStatus = "succeeded";
      })
      .addCase(fetchManagementSkills.rejected, (state, action) => {
        state.managementSkillsStatus = "failed";
        state.managementSkillsError = action.error.message || "Failed to fetch management skills.";
      });
  },
});

export const { clearHiringManagers, clearCountries, clearCities, clearKeySkills } = filterSlice.actions;
export const fetchFiltersDepartments = fetchDepartments;
export default filterSlice.reducer;
